// The player's side panel (DS PlayerScreen / WatchPage, SD35): a white card with DS Tabs —
// underline in the app, pill on the public page — holding Transcript and Chapters.
// FR-PL3 / FR-P6: the transcript follows playback — the line under the playhead is highlighted,
// the list scrolls to keep it in view unless the viewer scrolled it themselves in the last 5 s,
// and clicking a line seeks. Missing AI is an empty state, not an error (FR-AI7). F12: every
// recording can have chapters now (manual, and generated once an LLM is connected) — owners and
// editors get full CRUD here, gated on `canEdit`. FR-M5a: the same editors can name the
// diarized speakers ("Speaker 2" → "Priya") from a row above the transcript, and FR-M5b: merge
// one into another when diarization split one person in two, undo that, and regenerate notes
// made before the merge.
import { useEffect, useRef, useState } from "react";
import { Button, Callout, Card, Icon, IconButton, Input, Select, Tabs, TranscriptLine } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { NotesGenerateOut, TranscriptOut, TranscriptStatus } from "../api/types";
import type { GrackleTranscript } from "../live/types";
import ConfirmDialog from "../components/ConfirmDialog";
import { segmentAnchorId, segmentKey, transcriptProvenanceLine } from "./transcriptView";
import { activeCueIndex, CHAPTERS_EMPTY, clock, transcriptSpeakers, transcriptText, type Cue } from "./transcript";
import { captionsUrl, chapterIndexAt, provenanceLine, type Chapter, type ChaptersResponse } from "./chapters";

/** A viewer who scrolled the list is reading something; leave it alone this long. */
export const USER_SCROLL_HOLD_MS = 5000;

/** The nudge buttons' step (brief scope 2: "simple -/+ buttons ... snap happens server-side") —
 * matches the player's own `ArrowLeft`/`ArrowRight` seek step for one consistent "a little" unit. */
export const NUDGE_MS = 5000;

export default function TranscriptPanel({
  tabsVariant = "underline",
  status,
  transcript,
  cues,
  currentTime,
  onSeek,
  emptyHint,
  chapters = [],
  grackleId,
  canEdit = false,
  onChaptersChange,
  onTranscriptChange,
  onNotesRegenerate,
  captionsFor,
}: {
  /** DS: underline tabs in the app's player, pill tabs on the public watch page. */
  tabsVariant?: "underline" | "pill";
  status: TranscriptStatus;
  transcript: GrackleTranscript | null;
  cues: Cue[];
  currentTime: number;
  onSeek: (seconds: number) => void;
  /** Shown under "No transcript yet." — e.g. what Transcribe does, for someone who can run it. */
  emptyHint?: string;
  /** F12: manual and generated chapters, sorted by start. */
  chapters?: Chapter[];
  /** F12 scope 2: needed to call the chapters CRUD endpoints. */
  grackleId?: string;
  /** F12 scope 2: owner/editors can add, rename, delete, nudge and clear generated chapters. */
  canEdit?: boolean;
  /** Told about every successful mutation, so the Player's `ChapterBar` (which reads the same
   * list from a shared parent) stays in sync without this panel owning the source of truth. */
  onChaptersChange?: (chapters: ChaptersResponse) => void;
  /** FR-M5a: told the transcript the server returned after a speaker rename (new names on
   * every segment, re-rendered captions), so the parent's `transcript` state follows. */
  onTranscriptChange?: (transcript: GrackleTranscript) => void;
  /** FR-M5b: told when this panel started regenerating notes, so the notes panel reloads. */
  onNotesRegenerate?: () => void;
  /** F12 scope 4: offer "Download captions (.vtt / .srt)" for this recording (view access). */
  captionsFor?: { grackleId: string; share?: string | null };
}) {
  const [tab, setTab] = useState("transcript");
  // DS TranscriptPanel: "Search transcript" filters the lines client-side; the count says how
  // many match. Follow-along keeps working on the lines that are still shown.
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const matches = (c: Cue) => !needle || `${c.speaker ?? ""} ${c.text}`.toLowerCase().includes(needle);
  const shownCount = needle ? cues.filter(matches).length : cues.length;
  const listRef = useRef<HTMLDivElement>(null);
  const userScrolledAt = useRef(0);
  const programmatic = useRef(false);
  const active = activeCueIndex(cues, currentTime);

  useEffect(() => {
    const list = listRef.current;
    if (!list || active < 0) return;
    if (Date.now() - userScrolledAt.current < USER_SCROLL_HOLD_MS) return;
    const line = list.querySelector<HTMLElement>(`[data-cue="${active}"]`);
    if (!line) return;
    const target = Math.max(0, line.offsetTop - list.offsetTop - list.clientHeight / 3);
    if (Math.abs(list.scrollTop - target) < 2) return;
    programmatic.current = true;
    list.scrollTop = target;
  }, [active, tab]);

  function onScroll() {
    // Our own scrollTop writes fire scroll too; only a viewer's scroll holds the follow.
    if (programmatic.current) {
      programmatic.current = false;
      return;
    }
    userScrolledAt.current = Date.now();
  }

  function markUserScroll() {
    userScrolledAt.current = Date.now();
  }

  // ---- chapters CRUD (F12 scope 2): self-contained, same pattern as NotesPanel's calls,
  // reporting every change up through `onChaptersChange` so the Player (which reads the same
  // list from the shared parent) stays in sync. ----------------------------------------------
  const [newChapterTitle, setNewChapterTitle] = useState("Chapter");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameTitle, setRenameTitle] = useState("");
  const [chaptersError, setChaptersError] = useState<string | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  async function reloadChapters() {
    if (!grackleId) return;
    try {
      onChaptersChange?.(await api<ChaptersResponse>("GET", `/grackles/${grackleId}/chapters`));
    } catch {
      // A transient reload failure just leaves the last-known list in place.
    }
  }

  async function addChapter() {
    if (!grackleId) return;
    setChaptersError(null);
    try {
      const res = await api<ChaptersResponse>("POST", `/grackles/${grackleId}/chapters`, {
        start_ms: Math.round(currentTime * 1000),
        title: newChapterTitle.trim() || "Chapter",
      });
      onChaptersChange?.(res);
      setNewChapterTitle("Chapter");
    } catch (err) {
      setChaptersError(err instanceof ApiFail ? err.message : "Couldn't add the chapter.");
    }
  }

  async function commitRename(id: string) {
    const title = renameTitle.trim();
    setRenamingId(null);
    if (!grackleId || !title) return;
    setChaptersError(null);
    try {
      onChaptersChange?.(
        await api<ChaptersResponse>("PATCH", `/grackles/${grackleId}/chapters/${id}`, { title }),
      );
    } catch (err) {
      setChaptersError(err instanceof ApiFail ? err.message : "Couldn't rename the chapter.");
    }
  }

  async function nudgeChapter(c: Chapter, deltaMs: number) {
    if (!grackleId) return;
    setChaptersError(null);
    try {
      onChaptersChange?.(
        await api<ChaptersResponse>("PATCH", `/grackles/${grackleId}/chapters/${c.id}`, {
          start_ms: Math.max(0, c.start_ms + deltaMs),
        }),
      );
    } catch (err) {
      setChaptersError(err instanceof ApiFail ? err.message : "Couldn't move the chapter.");
    }
  }

  async function removeChapter(id: string) {
    if (!grackleId) return;
    setChaptersError(null);
    try {
      await api("DELETE", `/grackles/${grackleId}/chapters/${id}`);
      await reloadChapters();
    } catch (err) {
      setChaptersError(err instanceof ApiFail ? err.message : "Couldn't delete the chapter.");
    }
  }

  async function clearGenerated() {
    setClearConfirmOpen(false);
    if (!grackleId) return;
    setChaptersError(null);
    try {
      await api("DELETE", `/grackles/${grackleId}/chapters?source=generated`);
      await reloadChapters();
    } catch (err) {
      setChaptersError(err instanceof ApiFail ? err.message : "Couldn't clear generated chapters.");
    }
  }

  const hasGeneratedChapters = chapters.some((c) => c.source === "generated");

  // ---- speaker names (FR-M5a): one PATCH per rename, keyed by the provider tag; the server
  // answers with the whole transcript (names applied, captions re-rendered), handed up through
  // `onTranscriptChange` so the cues this panel renders come from the parent as before. ------
  const speakers = transcriptSpeakers(transcript);
  const [renamingSpeaker, setRenamingSpeaker] = useState<string | null>(null);
  const [speakerName, setSpeakerName] = useState("");
  const [speakersError, setSpeakersError] = useState<string | null>(null);
  // FR-M5b: the speaker being merged and the one it goes into.
  const [mergingSpeaker, setMergingSpeaker] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [notesRegenerating, setNotesRegenerating] = useState(false);

  async function patchSpeakers(body: Record<string, unknown>, failure: string) {
    if (!grackleId) return;
    setSpeakersError(null);
    try {
      const res = await api<TranscriptOut>("PATCH", `/grackles/${grackleId}/transcript/speakers`, body);
      onTranscriptChange?.(res as GrackleTranscript);
    } catch (err) {
      setSpeakersError(err instanceof ApiFail ? err.message : failure);
    }
  }

  async function commitSpeakerName(tag: string, name: string | null) {
    setRenamingSpeaker(null);
    await patchSpeakers({ names: { [tag]: name } }, "Couldn't rename the speaker.");
  }

  async function commitMerge(from: string, into: string | null) {
    setMergingSpeaker(null);
    setNotesRegenerating(false);
    await patchSpeakers({ merges: { [from]: into } }, into ? "Couldn't merge the speakers." : "Couldn't unmerge the speaker.");
  }

  async function regenerateNotes() {
    if (!grackleId) return;
    setSpeakersError(null);
    try {
      await api<NotesGenerateOut>("POST", `/grackles/${grackleId}/notes/generate`, { force: true });
      setNotesRegenerating(true);
      onNotesRegenerate?.();
    } catch (err) {
      setSpeakersError(err instanceof ApiFail ? err.message : "Couldn't start regenerating notes.");
    }
  }

  return (
    <Card as="aside" size="flush" className="watch-panel" aria-label="Transcript and chapters">
      <Tabs
        variant={tabsVariant}
        value={tab}
        onChange={setTab}
        className="watch-panel__tabs"
        items={[
          { value: "transcript", label: "Transcript" },
          { value: "chapters", label: "Chapters" },
        ]}
      />
      {tab === "transcript" && status === "ready" && cues.length > 0 ? (
        <div className="watch-panel__search">
          <Input
            size="sm"
            icon="magnifying-glass"
            placeholder="Search transcript"
            aria-label="Search transcript"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // min-width 0 lets the field shrink in a narrow panel instead of pushing the count
            // and the copy button past the card's edge.
            wrapperStyle={{ flex: 1, minWidth: 0 }}
          />
          <span className="watch-panel__count gk-caption gk-num" aria-live="polite">
            {needle ? `${shownCount} of ${cues.length}` : `${cues.length} lines`}
          </span>
          <CopyTranscriptButton cues={cues} />
        </div>
      ) : null}
      {captionsFor && status === "ready" && cues.length > 0 ? (
        <div className="watch-panel__downloads">
          Download captions:{" "}
          <a href={captionsUrl("/api/v1", captionsFor.grackleId, "vtt", captionsFor.share)} download>
            .vtt
          </a>{" "}
          /{" "}
          <a href={captionsUrl("/api/v1", captionsFor.grackleId, "srt", captionsFor.share)} download>
            .srt
          </a>
        </div>
      ) : null}
      {tab === "chapters" && canEdit ? (
        <div className="watch-chapters__toolbar">
          <Input
            size="sm"
            value={newChapterTitle}
            onChange={(e) => setNewChapterTitle(e.target.value)}
            placeholder="New chapter title"
            aria-label="New chapter title"
          />
          <Button size="sm" variant="secondary" icon="plus" onClick={() => void addChapter()}>
            Add at {clock(currentTime)}
          </Button>
          {hasGeneratedChapters ? (
            <Button size="sm" variant="ghost" onClick={() => setClearConfirmOpen(true)}>
              Clear generated chapters
            </Button>
          ) : null}
        </div>
      ) : null}
      {tab === "chapters" && chaptersError ? <p className="watch-chapters__error gk-caption">{chaptersError}</p> : null}
      {tab === "transcript" && canEdit && grackleId && status === "ready" && speakers.length > 0 ? (
        <div className="watch-speakers" role="group" aria-label="Speakers">
          <span className="watch-speakers__label gk-caption">Speakers</span>
          {speakers.map((sp) =>
            renamingSpeaker === sp.tag ? (
              <span key={sp.tag} className="watch-speakers__row watch-speakers__row--editing">
                <Input
                  size="sm"
                  autoFocus
                  value={speakerName}
                  maxLength={80}
                  aria-label={`Name for ${sp.name}`}
                  placeholder={sp.name}
                  onChange={(e) => setSpeakerName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void commitSpeakerName(sp.tag, speakerName);
                    if (e.key === "Escape") setRenamingSpeaker(null);
                  }}
                />
                <IconButton
                  icon="check"
                  label="Save speaker name"
                  size="sm"
                  iconSize={16}
                  onClick={() => void commitSpeakerName(sp.tag, speakerName)}
                />
                <IconButton icon="x" label="Cancel" size="sm" iconSize={16} onClick={() => setRenamingSpeaker(null)} />
              </span>
            ) : mergingSpeaker === sp.tag ? (
              <span key={sp.tag} className="watch-speakers__row watch-speakers__row--editing">
                <span className="watch-speakers__merge-label">Merge {sp.name} into</span>
                <Select
                  size="sm"
                  aria-label={`Speaker to merge ${sp.name} into`}
                  placeholder="Choose a speaker"
                  value={mergeTarget}
                  onChange={setMergeTarget}
                  options={speakers.filter((o) => o.tag !== sp.tag).map((o) => ({ value: o.tag, label: o.name }))}
                />
                <IconButton
                  icon="check"
                  label="Merge speakers"
                  size="sm"
                  iconSize={16}
                  disabled={!mergeTarget}
                  onClick={() => void commitMerge(sp.tag, mergeTarget)}
                />
                <IconButton icon="x" label="Cancel" size="sm" iconSize={16} onClick={() => setMergingSpeaker(null)} />
              </span>
            ) : (
              <span key={sp.tag} className="watch-speakers__row">
                <b className="watch-panel__speaker">{sp.name}</b>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Rename ${sp.name}`}
                  onClick={() => {
                    setMergingSpeaker(null);
                    setRenamingSpeaker(sp.tag);
                    setSpeakerName(sp.custom ? sp.name : "");
                  }}
                >
                  Rename
                </Button>
                {sp.custom ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Reset ${sp.name} to its default name`}
                    onClick={() => void commitSpeakerName(sp.tag, null)}
                  >
                    Reset
                  </Button>
                ) : null}
                {speakers.length > 1 ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Merge ${sp.name} into another speaker`}
                    onClick={() => {
                      setRenamingSpeaker(null);
                      setMergingSpeaker(sp.tag);
                      setMergeTarget("");
                    }}
                  >
                    Merge
                  </Button>
                ) : null}
              </span>
            ),
          )}
          {speakers.flatMap((sp) =>
            sp.merged.map((m) => (
              <span key={m.tag} className="watch-speakers__row watch-speakers__merged">
                <span className="gk-caption">
                  {m.name} merged into {sp.name}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Unmerge ${m.name} from ${sp.name}`}
                  onClick={() => void commitMerge(m.tag, null)}
                >
                  Unmerge
                </Button>
              </span>
            )),
          )}
        </div>
      ) : null}
      {tab === "transcript" && canEdit && grackleId && status === "ready" && transcript?.notes_predate_merge ? (
        <Callout tone="info" className="watch-speakers__notes">
          {notesRegenerating ? (
            "Regenerating notes."
          ) : (
            <>
              These notes were made before the last speaker merge, so they may not match the transcript.{" "}
              <Button size="sm" variant="secondary" onClick={() => void regenerateNotes()}>
                Regenerate notes
              </Button>
            </>
          )}
        </Callout>
      ) : null}
      {tab === "transcript" && speakersError ? <p className="watch-chapters__error gk-caption">{speakersError}</p> : null}
      {tab === "chapters" && chapters.length > 0 ? (
        <div className="watch-panel__body" role="tabpanel" aria-label="Chapters">
          <ol className="watch-chapters">
            {chapters.map((c, i) =>
              renamingId === c.id ? (
                <li key={c.id} className="watch-chapters__row watch-chapters__row--editing">
                  <span className="gk-num">{clock(c.start_ms / 1000)}</span>
                  <Input
                    size="sm"
                    autoFocus
                    value={renameTitle}
                    aria-label="Chapter title"
                    onChange={(e) => setRenameTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void commitRename(c.id);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                  />
                  <IconButton icon="check" label="Save" size="sm" iconSize={16} onClick={() => void commitRename(c.id)} />
                  <IconButton icon="x" label="Cancel" size="sm" iconSize={16} onClick={() => setRenamingId(null)} />
                </li>
              ) : (
                <li key={c.id} className="watch-chapters__row">
                  <button
                    type="button"
                    className="watch-chapters__item"
                    aria-current={i === chapterIndexAt(chapters, currentTime) ? "true" : undefined}
                    onClick={() => onSeek(c.start_ms / 1000)}
                  >
                    <span className="gk-num">{clock(c.start_ms / 1000)}</span> <span>{c.title}</span>
                  </button>
                  {canEdit ? (
                    <span className="watch-chapters__row-actions">
                      <IconButton
                        icon="minus"
                        label="Move chapter earlier"
                        size="sm"
                        iconSize={16}
                        onClick={() => void nudgeChapter(c, -NUDGE_MS)}
                      />
                      <IconButton
                        icon="plus"
                        label="Move chapter later"
                        size="sm"
                        iconSize={16}
                        onClick={() => void nudgeChapter(c, NUDGE_MS)}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Rename chapter"
                        onClick={() => {
                          setRenamingId(c.id);
                          setRenameTitle(c.title);
                        }}
                      >
                        Rename
                      </Button>
                      <IconButton
                        icon="trash"
                        label="Delete chapter"
                        size="sm"
                        iconSize={16}
                        onClick={() => void removeChapter(c.id)}
                      />
                    </span>
                  ) : null}
                </li>
              ),
            )}
          </ol>
          {/* Brief scope 5: provenance once for generated chapters, per chapter for manual. */}
          {Array.from(new Set(chapters.map(provenanceLine).filter(Boolean))).map((line) => (
            <p key={line} className="watch-chapters__provenance">
              {line}
            </p>
          ))}
        </div>
      ) : tab === "chapters" ? (
        <div className="watch-panel__empty" role="tabpanel" aria-label="Chapters">
          <Icon name="sparkle" />
          <p>{CHAPTERS_EMPTY}.</p>
        </div>
      ) : status === "queued" || status === "running" ? (
        <div className="watch-panel__empty" role="tabpanel" aria-label="Transcript">
          <Icon name="circle-notch" className="spin" />
          <p>{status === "queued" ? "Transcript queued." : "Transcribing."} This panel updates when it's ready.</p>
        </div>
      ) : status === "failed" ? (
        <div className="watch-panel__empty" role="tabpanel" aria-label="Transcript">
          <Icon name="warning-circle" />
          <p>{transcript?.error || "Transcription failed."} The recording is unchanged.</p>
        </div>
      ) : status !== "ready" ? (
        <div className="watch-panel__empty" role="tabpanel" aria-label="Transcript">
          <Icon name="subtitles" />
          <p>No transcript yet.{emptyHint ? ` ${emptyHint}` : ""}</p>
        </div>
      ) : cues.length === 0 ? (
        <div className="watch-panel__body" role="tabpanel" aria-label="Transcript">
          {transcript?.text ? (
            <p className="watch-panel__text">{transcript.text}</p>
          ) : (
            <div className="watch-panel__empty">
              <p>{transcript ? "The transcript is empty." : "Loading transcript…"}</p>
            </div>
          )}
        </div>
      ) : (
        <div
          ref={listRef}
          className="watch-panel__body"
          role="tabpanel"
          aria-label="Transcript"
          onScroll={onScroll}
          onWheel={markUserScroll}
          onTouchMove={markUserScroll}
        >
          {needle && shownCount === 0 ? (
            <div className="watch-panel__empty">
              <p>No lines match "{query.trim()}".</p>
            </div>
          ) : null}
          {cues.map((c, i) => (
            !matches(c) ? null : (
            <TranscriptLine
              key={c.id ? segmentKey({ id: c.id }, i) : `${i}-${c.start}`}
              id={c.id ? segmentAnchorId({ id: c.id }) : undefined}
              data-segment-id={c.id || undefined}
              data-cue={i}
              time={clock(c.start)}
              state={i < active ? "past" : i === active ? "current" : "future"}
              text={
                c.speaker ? (
                  <>
                    <b className="watch-panel__speaker">{c.speaker}</b> {c.text}
                  </>
                ) : (
                  c.text
                )
              }
              onClick={() => onSeek(c.start)}
            />
            )
          ))}
        </div>
      )}
      {status === "ready" && transcript && transcriptProvenanceLine(transcript) ? (
        <div className="watch-panel__foot gk-caption">
          <Icon name="waveform" size={15} />
          <span>
            {transcriptProvenanceLine(transcript)}
            {transcript.model_id ? " · " : ""}
            {transcript.model_id ? <span className="mono">{transcript.model_id}</span> : null}
          </span>
        </div>
      ) : null}
      {status === "ready" && transcript?.diarization_status === "degraded" && transcript.diarization_error ? (
        <div className="watch-panel__foot gk-caption">
          <Icon name="user-sound" size={15} />
          <span>{transcript.diarization_error}</span>
        </div>
      ) : null}
      <ConfirmDialog
        open={clearConfirmOpen}
        title="Clear generated chapters"
        message="Remove every generated chapter? Chapters you added yourself are kept."
        confirmLabel="Clear generated"
        danger
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={() => void clearGenerated()}
      />
    </Card>
  );
}

const COPY_FEEDBACK_MS = 2000;

/** Copies every line, not just the ones a search is showing — the label says "transcript".
 * Icon-only: the search row in the 340px panel has no room for a text button. */
function CopyTranscriptButton({ cues }: { cues: Cue[] }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  async function copy() {
    let next: "copied" | "failed" = "copied";
    try {
      await navigator.clipboard.writeText(transcriptText(cues));
    } catch {
      // No Clipboard API (insecure context) or permission denied: say so rather than pretend.
      next = "failed";
    }
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState("idle"), COPY_FEEDBACK_MS);
  }
  return (
    <IconButton
      size="sm"
      icon={state === "copied" ? "check" : state === "failed" ? "warning-circle" : "copy"}
      label={state === "copied" ? "Copied" : state === "failed" ? "Couldn't copy" : "Copy transcript"}
      onClick={() => void copy()}
      className="watch-panel__copy"
    />
  );
}
